import { STANDARD_DISCLOSURE, type Partner } from "./types";

export const neighborworks: Partner = {
  slug: "neighborworks",
  name: "NeighborWorks America",
  fullName: "NeighborWorks America network",
  path: "/partners/neighborworks",
  externalUrl: "https://www.neighborworks.org/",
  externalLabel: "Find your NeighborWorks organization",

  title: "NeighborWorks: Counseling and Down Payment Help Under One Roof (2026)",
  description:
    "NeighborWorks America is a national network of local nonprofits that counsel buyers and, in many markets, administer down payment assistance themselves. Who it fits, how affiliates differ, and how it pairs with stacking grants and DPA.",
  h1: "NeighborWorks: the network that counsels you and funds you in the same office",

  cardHeadline: "Counseling and DPA from the same organization",
  cardBody:
    "NeighborWorks is a national network of roughly 250 local nonprofits. What makes it different from a plain counseling agency: many affiliates also administer down payment assistance and their own affordable lending, so the office that fixes your file can also be the one that funds part of your purchase.",
  cardPoints: ["Free counseling", "Many run local DPA", "~250 local affiliates", "Income limits vary"],

  barriers: ["savings", "credit", "certificate", "income_limits"],
  hubSummary:
    "A national network of local nonprofits where counseling, homebuyer education, and down payment assistance often live under the same roof.",

  fits: [
    "You want counseling and assistance from one organization instead of three.",
    "Your income is at or below the area limits most local programs use.",
    "You want a certificate from a provider assistance programs already recognize.",
    "You're early enough that a few months of preparation is fine.",
  ],
  doesNotFit: [
    "There's no affiliate in your market — check the locator first.",
    "Your income is well above area limits (look at no-income-limit DPA instead).",
    "You need to close immediately.",
  ],
  steps: [
    { title: "Find your nearest affiliate", detail: "Use the NeighborWorks locator. Each affiliate is independent, with its own programs, income caps, and funding calendar." },
    { title: "Ask what they administer", detail: "Counseling, education, DPA, affordable lending, and rehab programs vary by office. Ask for the full list." },
    { title: "Complete counseling and education", detail: "Free counseling plus the certificate most DPA programs require before closing." },
    { title: "Apply while funds are open", detail: "Local allocations run on a calendar and can be exhausted mid-year. Ask directly whether the program is currently funded." },
    { title: "Stack the remaining layers", detail: "Run your Stack to see what sits on top: state programs, employer benefits, and national DPA." },
  ],
  compareLabel: "NeighborWorks affiliate",
  compare: [
    { factor: "What you get", partner: "Counseling, education, often local DPA and lending", stack: "A list of grants and DPA you may qualify for" },
    { factor: "Cost", partner: "Free counseling; course fees vary", stack: "Free" },
    { factor: "Income limits", partner: "Common on program-based help", stack: "Shows both capped and uncapped programs" },
    { factor: "Coverage", partner: "Where an affiliate operates", stack: "Nationwide by ZIP" },
    { factor: "Timeline", partner: "Weeks to months", stack: "Minutes" },
    { factor: "Best used", partner: "When you want one organization end to end", stack: "Always, first" },
  ],
  faqs: [
    { q: "Is NeighborWorks a lender?", a: "The national organization is not. Some local affiliates are certified CDFIs or approved lenders and originate their own affordable mortgages; most do not. Ask the affiliate directly." },
    { q: "Does every affiliate offer down payment assistance?", a: "No. Affiliates are independent nonprofits with different program mixes, income caps, and funding. Two affiliates in neighboring counties can offer completely different help." },
    { q: "Are there income limits?", a: "Counseling and education are generally open to everyone. Program-based help — DPA, affordable lending, rehab — almost always uses area median income limits set locally." },
    { q: "Will their homebuyer course satisfy my DPA program?", a: "NeighborWorks-affiliated courses are widely accepted, but 'widely' is not 'always'. Get your program's approved-provider list in writing first." },
    { q: "Does Home Approach get paid for this referral?", a: "No. We surface NeighborWorks because counseling and funding in the same office is the shortest honest path for a lot of buyers." },
  ],
  disclosure: STANDARD_DISCLOSURE("NeighborWorks America"),
};
